import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const api = axios.create({ baseURL: API_BASE });

// Track refresh state to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise = null;

async function refreshToken() {
  if (isRefreshing) return refreshPromise;
  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return false;

      const resp = await axios.post(`${API_BASE}/client/refresh`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { access_token, csrf_token } = resp.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("csrf_token", csrf_token);
      return true;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Auto-refresh: check token expiry before each request
api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem("token");
  if (token) {
    // Check if token expires within 5 minutes
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiresIn = payload.exp * 1000 - Date.now();
      if (expiresIn < 5 * 60 * 1000 && expiresIn > -15 * 60 * 1000) {
        // Expires within 5min or expired within 15min grace — refresh
        const refreshed = await refreshToken();
        if (refreshed) {
          config.headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // Add CSRF token for state-changing requests
  const csrfToken = localStorage.getItem("csrf_token");
  if (csrfToken && ["post", "put", "delete", "patch"].includes(config.method?.toLowerCase())) {
    config.headers["X-CSRF-Token"] = csrfToken;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Try refresh once on 401
      const originalRequest = error.config;
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        const refreshed = await refreshToken();
        if (refreshed) {
          originalRequest.headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
          originalRequest.headers["X-CSRF-Token"] = localStorage.getItem("csrf_token");
          return api(originalRequest);
        }
      }

      // Refresh failed — redirect to login
      const authPages = ["/login", "/login/verify-2fa", "/register", "/verify", "/forgot-password"];
      if (!authPages.includes(window.location.pathname)) {
        localStorage.removeItem("token");
        localStorage.removeItem("csrf_token");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Extract a human-readable error message from an Axios error.
 * Handles FastAPI 422 responses where detail is an array of objects.
 */
export function getErrorMessage(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(", ");
  return fallback;
}

// ponytail: Blob.text() isn't implemented by every test/runtime Blob polyfill
// (jsdom's Blob lacks it); FileReader is the one blob-to-text path that's
// reliably present everywhere, so it's used instead of the newer method.
function _blobToText(blob) {
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * Same contract as getErrorMessage, but for requests made with
 * responseType:"blob" — on failure axios still puts the error body at
 * err.response.data, except it's a Blob (of the same JSON error payload)
 * instead of a parsed object, so .detail is unreachable without this.
 */
export async function getBlobErrorMessage(err, fallback = "Something went wrong") {
  const data = err?.response?.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      const parsed = JSON.parse(await _blobToText(data));
      if (typeof parsed?.detail === "string") return parsed.detail;
    } catch {
      // Not JSON, or unreadable — fall through to the fallback below.
    }
    return fallback;
  }
  return getErrorMessage(err, fallback);
}

export default api;
